import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import SkeletonComponent from "../../Common/SkeletonComponent";
import DashboardGraphEmptyState from "../../Common/DashboardGraphEmptyState";
import smsNoDataImg from "../../../assets/icons/sms-transparent.webp";
import { useSelector } from "react-redux";
import { reportSelector } from "../ReportSlice";
import { tableShortFormatDate } from "../../../Wrapper/CommonFun";
import { tableCustomStyles } from "../../../lib/utils";
import DataTablePaginationComponent from "../../Common/DataTablePaginationComponent";
import { filterDataByField } from "../../../lib/utility";

const SubmissionTable = ({
  submissionTableData,
  isFetching,
  country,
  status,
  searchVal,
  pagination,
  handlePageChange,
  handlePerPageChange,
}) => {
  const { t } = useTranslation("summaryDelivery");
  const { isFetchingSubmissionUser } = useSelector(reportSelector);

  const columns = useMemo(() => {
    const cols = [
      {
        name: t("submissionReport.table.columns.date", { defaultValue: "Date" }),
        selector: (row) => tableShortFormatDate(row.Date, false),
        sortFunction: (rowA, rowB) => {
          const dateA = new Date(rowA.Date);
          const dateB = new Date(rowB.Date);
          return dateA - dateB;
        },
        minWidth: "180px",
        sortable: true,
      },
      {
        name: t("submissionReport.table.columns.count", { defaultValue: "Count" }),
        selector: (row) => row.Count,
        sortable: true,
      },
    ];

    if (country && country !== "%") {
      cols.push({
        name: t("submissionReport.table.columns.country", {
          defaultValue: "Country",
        }),
        selector: (row) => row.Country,
        sortable: true,
      });
    }

    if (status && status !== "%") {
      cols.push({
        name: t("submissionReport.table.columns.status", {
          defaultValue: "Status",
        }),
        selector: (row) => row.Status,
        sortable: true,
      });
    }

    return cols;
  }, [country, status, t]);

  const data = useMemo(() => {
    if (!submissionTableData || !Array.isArray(submissionTableData)) {
      return [];
    }

    return submissionTableData.map((row) => ({
      Date: row.startDate || "",
      Count: row.totalSmsSend || 0,
      Country: row.country || "",
      Status: row.messageStatus || "",
    }));
  }, [submissionTableData]);

  const filteredData = useMemo(() => {
    if (!searchVal || searchVal.trim() === "") {
      return data;
    }
    return filterDataByField(data, "Date,Count,Country,Status", searchVal);
  }, [data, searchVal]);

  const paginatedData = useMemo(() => {
    const startIndex = pagination.current * pagination.size;
    const endIndex = startIndex + pagination.size;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, pagination.current, pagination.size]);

  const hasRows = filteredData.length > 0;

  return (
    <div className="m-3 border br-12 table-action frozen submission-table-wrapper">
      {isFetchingSubmissionUser || isFetching ? (
        <SkeletonComponent variant="table" />
      ) : (
        <>
          <div
            className={`submission-table-scroll-area${hasRows ? "" : " is-empty"}`}
          >
            <DataTable
              className="submission-table"
              columns={columns}
              data={paginatedData}
              highlightOnHover
              pagination={false}
              customStyles={{
                ...tableCustomStyles({
                  height: "auto",
                  minHeight: 0,
                  overflow: "visible",
                  tableStyle: {
                    height: "auto",
                    minHeight: 0,
                    overflow: "visible",
                  },
                  wrapperStyle: {
                    height: "auto",
                    minHeight: 0,
                    overflow: "visible",
                  },
                }),
                responsiveWrapper: {
                  style: {
                    overflow: "visible",
                  },
                },
              }}
              noDataComponent={
                <div
                  className="p-4 d-flex justify-content-center align-items-center"
                  style={{ minHeight: 300 }}
                >
                  <DashboardGraphEmptyState
                    imageSrc={smsNoDataImg}
                    title={t("noData", { ns: "common" })}
                  />
                </div>
              }
            />
          </div>
          {hasRows && (
            <DataTablePaginationComponent
              rowsPerPage={pagination.size}
              rowCount={filteredData.length}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerPageChange}
              currentPage={pagination.current + 1}
              paginationRowsPerPageOptions={[
                10, 30, 50, 100, 150, 200, 250, 350, 400, 450, 500,
              ]}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SubmissionTable;
